import React from 'react';
import { Box, Button, ButtonBase, TextField, Typography } from '@mui/material';
import { Building2, ChevronRight, SearchX } from 'lucide-react';
import { useIntl } from 'react-intl';
import CardCategories from './cardcategories';
import { normalizePortalText, PortalFilter, PortalPagination } from './portal-controls';
import { portalColors, portalFocus, portalInput, portalMotion } from '../portal-styles';

const PAGE_SIZE = 10;

function OrganizationCategories({ group }) {
  const intl = useIntl();
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const filtered = group.roots.filter(item => normalizePortalText(`${item.name} ${item.parentname || ''}`).includes(normalizePortalText(query.trim())));
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, pageCount));

  return <Box component="section" aria-labelledby="portal-selected-organization" sx={{ minWidth: 0 }}>
    <Box sx={{ mb: 3 }}>
      <Typography id="portal-selected-organization" component="h2" aria-live="polite" aria-atomic="true" sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 500, lineHeight: 1.3, letterSpacing: '-0.025em', color: portalColors.ink, overflowWrap: 'anywhere' }}>
        {group.organizationname}
      </Typography>
      <Typography variant="body2" sx={{ color: portalColors.muted, mt: 1 }} aria-live="polite" aria-atomic="true">
        {intl.formatMessage({ id: 'portal.category.count' }, { count: group.rootCount })}
        {' · '}{intl.formatMessage({ id: 'portal.categories.hint' })}
      </Typography>
    </Box>
    {group.roots.length > PAGE_SIZE && <Box sx={{ mb: 2 }}>
      <PortalFilter id="portal-organization-category-filter" label={intl.formatMessage({ id: 'portal.categories.filter' })}
        value={query} onChange={value => { setQuery(value); setPage(1); }} />
    </Box>}
    {filtered.length ? <Box sx={{ display: 'grid', gridTemplateColumns: filtered.length === 1 ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', columnGap: 3 }}>
      {filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(category => <Box key={category.id} sx={{ borderBottom: `1px solid ${portalColors.line}`, py: 0.75, minWidth: 0 }}>
        <CardCategories element={category} variant="category" />
      </Box>)}
    </Box> : <Box role="status" sx={{ py: 5, color: portalColors.muted }}>
      <SearchX size={28} strokeWidth={1.5} aria-hidden="true" />
      <Typography sx={{ mt: 1.5 }}>{intl.formatMessage({ id: query ? 'portal.search.no.results' : 'portal.organization.empty' })}</Typography>
      {query && <Button color="inherit" onClick={() => { setQuery(''); setPage(1); }} sx={{ ...portalFocus, mt: 1, color: portalColors.ink, textTransform: 'none' }}>{intl.formatMessage({ id: 'portal.category.search.clear' })}</Button>}
    </Box>}
    <PortalPagination page={currentPage} count={pageCount} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
  </Box>;
}

export default function PortalOrganizations({ organizations, selectedOrganizationId, onSelectOrganization }) {
  const intl = useIntl();
  const [query, setQuery] = React.useState('');
  const selected = organizations.find(group => String(group.organizationid) === String(selectedOrganizationId)) || organizations[0];
  const filtered = organizations.filter(group => normalizePortalText(group.organizationname).includes(normalizePortalText(query.trim())));
  if (!selected) return null;

  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '264px minmax(0, 1fr)' }, gap: { xs: 3.5, md: 5 }, alignItems: 'start' }}>
    <Box component="nav" aria-label={intl.formatMessage({ id: 'portal.organizations.title' })} sx={{ minWidth: 0 }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, bgcolor: portalColors.surface, borderRadius: '20px', p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pt: 1, pb: 2 }}>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 500, color: portalColors.muted }}>{intl.formatMessage({ id: 'portal.organizations.title' })}</Typography>
          <Typography variant="body2" sx={{ color: portalColors.muted }}>{organizations.length}</Typography>
        </Box>
        {organizations.length > 6 && <Box sx={{ mb: 1.5 }}><PortalFilter id="portal-organizations-filter" label={intl.formatMessage({ id: 'portal.organizations.filter' })} value={query} onChange={setQuery} /></Box>}
        <Box sx={{ maxHeight: 480, overflowY: 'auto', p: 0.5 }}>
          {filtered.map(group => {
            const active = group.organizationid === selected.organizationid;
            return <ButtonBase key={group.organizationid} onClick={() => onSelectOrganization(group.organizationid)} aria-current={active ? 'true' : undefined}
              sx={{ ...portalFocus, ...portalMotion, width: '100%', minHeight: 64, display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr) 16px', gap: 1.25, p: 1.5, mb: 0.5,
                textAlign: 'left', borderRadius: '14px', color: active ? portalColors.action : portalColors.ink, bgcolor: active ? portalColors.soft : 'transparent',
                boxShadow: active ? `inset 0 0 0 1px ${portalColors.light}` : 'none', transition: 'background-color 180ms ease-out, box-shadow 180ms ease-out',
                '&:hover': { bgcolor: active ? portalColors.soft : portalColors.paper },
              }}>
              <Building2 size={20} strokeWidth={1.6} color={active ? portalColors.action : portalColors.muted} aria-hidden="true" />
              <Box component="span" sx={{ minWidth: 0 }}>
                <Typography component="span" sx={{ display: 'block', fontSize: 14, fontWeight: active ? 600 : 400, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{group.organizationname}</Typography>
                <Typography component="span" sx={{ display: 'block', fontSize: 12, color: active ? portalColors.action : portalColors.muted, mt: 0.5 }}>{intl.formatMessage({ id: 'portal.category.count' }, { count: group.rootCount })}</Typography>
              </Box>
              {active && <ChevronRight size={16} color={portalColors.action} aria-hidden="true" />}
            </ButtonBase>;
          })}
          {!filtered.length && <Box role="status" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ color: portalColors.muted }}>{intl.formatMessage({ id: 'portal.organizations.empty' })}</Typography>
            <Button color="inherit" onClick={() => setQuery('')} sx={{ ...portalFocus, textTransform: 'none', color: portalColors.ink }}>{intl.formatMessage({ id: 'portal.category.search.clear' })}</Button>
          </Box>}
        </Box>
      </Box>
      <TextField select fullWidth label={intl.formatMessage({ id: 'portal.organizations.title' })}
        value={String(selected.organizationid)} onChange={event => onSelectOrganization(event.target.value)}
        SelectProps={{ native: true }} sx={{ ...portalInput, display: { xs: 'block', md: 'none' }, '& select': { whiteSpace: 'normal', fontSize: 16 } }}>
        {organizations.map(group => <option key={group.organizationid} value={String(group.organizationid)}>{group.organizationname}</option>)}
      </TextField>
    </Box>
    <OrganizationCategories key={selected.organizationid} group={selected} />
  </Box>;
}
